import {
  Component,
  DestroyRef,
  effect,
  Input,
  OnInit,
  OnDestroy,
  Signal,
  inject,
  signal,
  Output,
  EventEmitter,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { ControlService } from '../library/control.service';
import { RealtimeService } from '../library/realtime/realtime.service';
import { UserVideoService } from '../library/user-video.service';
import { DeviceSettingsService } from '../library/device-settings.service';
import { PlayerService } from '../library/player.service';
import { CallStoreService } from '../library/call-store/call-store.service';
import { PathwayConstants } from 'src/app/utils/constants/pathway/pathway.constants';
import { TranslationService } from 'src/app/services/translation.service';
import { TRANSLOCO_SCOPE } from '@jsverse/transloco';
import { IPathwayRunStepResponse } from 'src/app/interfaces/ipathway-enrollment';
import { PathwayRoleplayService } from './services/pathway-roleplay.service';
import {
  PathwayStepType,
  RoleplayFooterState,
} from 'src/app/utils/enums/learner/pathway.enum';
import { ICallRealtime } from 'src/app/interfaces/icall-realtime';
import { SubtitleKind } from 'src/app/utils/enums/learner/subtitle-kind.enum';
import { PathwayRoleplayFooterService } from './services/pathway-roleplay-footer.service';
import { PathwayRoleplayResultsService } from 'src/app/pages/learner/results/results-pathway-roleplay/service/pathway-roleplay-results.service';
import { PathwayLocalStorageUtil } from 'src/app/utils/pathway-localstorage.util';

@Component({
  host: {
    class: 'theme-dark',
  },
  selector: 'app-call-pathway-roleplay',
  standalone: false,
  templateUrl: './call-pathway-roleplay.component.html',
  styleUrls: ['./call-pathway-roleplay.component.scss'],
  providers: [
    PathwayRoleplayService,
    PathwayRoleplayFooterService,
    PathwayRoleplayResultsService,
    { provide: TRANSLOCO_SCOPE, useValue: 'learningPathway' },
    { provide: TRANSLOCO_SCOPE, useValue: 'call' },
  ],
})
export class CallPathwayRoleplayComponent implements OnInit, OnDestroy {
  @Input() controlService: ControlService | undefined;
  @Input() realtimeService: RealtimeService | undefined;
  @Input() userVideoService: UserVideoService | undefined;
  @Input() deviceSettingsService: DeviceSettingsService | undefined;
  @Input() playerService: PlayerService | undefined;
  @Input() callStore: CallStoreService | undefined;
  @Input() enrollmentStepUuid: string = '';
  @Input() enrollmentUuid: string = '';
  @Input() stepType: string = PathwayConstants.STEP_TYPE_INTRO;
  @Input() callUuid: string = '';
  @Input() callType: string = '';
  @Input() runStepResponse: Signal<IPathwayRunStepResponse | null> | undefined;
  @Input() title: string = '';
  @Input() endedEarly: boolean = false;

  isLoading = signal(false);
  readonly isResultScreen = signal(false);
  readonly hasDebriefError = signal(false);
  readonly showExitModal = signal(false);
  readonly exitModalIsDebrief = signal(false);
  private readonly exitModalFromHeader = signal(false);
  readonly exitModalTitleKey = signal('learningPathway.EXIT_MODAL_TITLE');
  readonly exitModalDescriptionKey = signal(
    'learningPathway.EXIT_MODAL_DESC_UNLIMITED_ATTEMPTS',
  );
  readonly resultsCallUuid = signal<string>('');
  readonly resultsCallData = signal<ICallRealtime | null>(null);
  private readonly pendingDebriefCallData = signal<ICallRealtime | null>(null);
  protected moduleAssignmentUuid = '';

  readonly subtitleKind = SubtitleKind.Ready;

  private readonly destroyRef = inject(DestroyRef);

  get mainIsLoading() {
    return this.roleplayService.mainIsLoading;
  }
  get isPreamble() {
    return this.roleplayService.isPreamble;
  }
  get callDetails() {
    return this.roleplayService.callDetails;
  }
  get captionText() {
    return this.roleplayService.captionText;
  }
  protected get isStepTypePracticeCall() {
    return this.stepType === PathwayStepType.PRACTICE_CALL;
  }
  protected get shouldShowCallDetails() {
    return (
      this.isStepTypePracticeCall &&
      !this.isPreamble() &&
      !!this.callDetails()?.callDetails?.length &&
      !this.isResultScreen()
    );
  }

  private get interactionsMet(): boolean {
    return (
      (this.controlService?.interactionCount ?? 0) >=
      (this.controlService?.interactionRequirement ?? 4)
    );
  }

  private get hasLimitedAttempts(): boolean {
    const maxAttempts = this.callStore?.getMaxAttemptsAllowed() ?? null;
    return maxAttempts != null && maxAttempts > 0;
  }

  @Output() settingsClicked = new EventEmitter<void>();
  @Output() leaveStepClicked = new EventEmitter<void>();
  /** Parent should navigate home without Tavus/backend leave APIs (results / post-call UX). */
  @Output() returnToPathway = new EventEmitter<void>();
  @Output() retryRoleplayClicked = new EventEmitter<void>();
  @Output() confirmActionClicked = new EventEmitter<(() => void) | undefined>();

  constructor(
    public readonly roleplayService: PathwayRoleplayService,
    public readonly footerService: PathwayRoleplayFooterService,
    private readonly route: ActivatedRoute,
    private readonly translationService: TranslationService,
    private readonly resultsService: PathwayRoleplayResultsService,
  ) {
    if (this.route.snapshot.queryParams['startDebrief'] === 'true') {
      effect(() => {
        const callData = this.pendingDebriefCallData();
        if (this.resultsService.resultReady() && callData) {
          this.resultsCallData.set(callData);
          this.resultsCallUuid.set(callData.practiceCallUuid);
          this.pendingDebriefCallData.set(null);
        }
      });
    }
  }

  ngOnInit(): void {
    this.footerService.init({
      controlService: this.controlService,
      callStore: this.callStore,
      deviceSettingsService: this.deviceSettingsService,
    });
    this.translationService.loadScopedTranslation('learningPathway');
    this.translationService.loadScopedTranslation('call');

    this.moduleAssignmentUuid =
      this.callStore?.getModuleAssignmentUuid() ||
      this.route.snapshot.queryParams['moduleAssignmentUuid'] ||
      '';

    const queryParams = this.route.snapshot.queryParams;
    const shouldHandleResultsFlow =
      queryParams['viewResults'] === 'true' ||
      queryParams['startDebrief'] === 'true';
    const practiceCallUuid = queryParams['practiceCallUuid'] ?? '';

    const initParams = {
      moduleAssignmentUuid: this.moduleAssignmentUuid,
      enrollmentStepUuid: this.enrollmentStepUuid,
      enrollmentUuid: this.enrollmentUuid,
      stepType: this.stepType,
      runStepResponse: this.runStepResponse,
      realtimeService: this.realtimeService,
      controlService: this.controlService,
      callStore: this.callStore,
    };

    if (shouldHandleResultsFlow && practiceCallUuid) {
      this.resultsCallUuid.set(practiceCallUuid);
      this.roleplayService.practiceCallUuid.set(practiceCallUuid);
      this.isResultScreen.set(true);
      this.roleplayService.footerState.set(RoleplayFooterState.ExitPathway);

      if (queryParams['startDebrief'] === 'true') {
        this.initAndStartDebrief(initParams);
      }

      return;
    }

    this.roleplayService.init(initParams);
    this.footerService.syncVideoState();

    this.roleplayService.onNavigateHome$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.returnToPathway.emit());
  }

  handleSettingsClick(): void {
    this.settingsClicked.emit();
  }

  handleLeaveStepClick(): void {
    const isDebriefActive = !!this.roleplayService.debriefInstanceUuid();
    if (isDebriefActive) {
      const cached = this.roleplayService.cachedDebriefResponse();
      if (!cached) {
        this.openDebriefExitModal();
        return;
      }
      this.roleplayService.prepareForNextStep();
      this.terminateDebriefConversation();
      this.roleplayService.mainIsLoading.set(true);
      this.leaveStepClicked.emit();
      return;
    }

    if (this.roleplayService.isPostCallVisible() || this.isResultScreen()) {
      this.roleplayService.mainIsLoading.set(true);
      this.returnToPathway.emit();
      return;
    }

    if (
      !this.footerService.isEnd() ||
      this.roleplayService.isPreamble() ||
      this.stepType === PathwayConstants.STEP_TYPE_INTRO
    ) {
      this.roleplayService.mainIsLoading.set(true);
      this.leaveStepClicked.emit();
      return;
    }

    this.exitModalFromHeader.set(true);
    this.openEndCallModal();
  }

  private initAndStartDebrief(
    initParams: Parameters<PathwayRoleplayService['init']>[0],
  ): void {
    this.roleplayService.init({ ...initParams, startDebrief: true });
    this.startDebrief((callData) => this.pendingDebriefCallData.set(callData));
  }

  private startDebrief(onSuccess: (callData: ICallRealtime) => void): void {
    this.roleplayService.isDebriefStarting.set(true);
    this.roleplayService
      .runDebiefAndStartGetRealTimeCallInterface()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (callData) => {
          onSuccess(callData);
          this.roleplayService.footerState.set(this.resolvePostDebriefState());
          this.roleplayService.isDebriefStarting.set(false);
        },
        error: () => {
          this.roleplayService.isDebriefStarting.set(false);
          this.hasDebriefError.set(true);
        },
      });
  }

  protected handleCallDetailsModalOpened(): void {
    this.realtimeService?.pauseCall();
  }

  protected handleCallDetailsModalClosed(): void {
    this.realtimeService?.unpauseCall();
  }

  handleLaunchSimulationClick(): void {
    this.roleplayService.launchSimulationFromPreamble();
  }

  handleExitConfirmed(): void {
    this.showExitModal.set(false);
    if (this.exitModalIsDebrief()) {
      this.exitModalIsDebrief.set(false);
      this.roleplayService.prepareForNextStep();
      this.terminateDebriefConversation();
      this.roleplayService.mainIsLoading.set(true);
      this.leaveStepClicked.emit();
      return;
    }
    if (this.exitModalFromHeader()) {
      this.exitModalFromHeader.set(false);
      // Mark UUID so the role-play step service polls for results on overview page
      const practiceCallUuid = this.roleplayService.practiceCallUuid();
      if (this.interactionsMet && practiceCallUuid) {
        PathwayLocalStorageUtil.markRoleplayPending(practiceCallUuid);
      }
      this.roleplayService.mainIsLoading.set(true);
      this.confirmActionClicked.emit(undefined);
      return;
    }
    if (!this.interactionsMet) {
      this.roleplayService.mainIsLoading.set(true);
    }
    this.confirmActionClicked.emit(
      this.interactionsMet
        ? () => {
            this.isResultScreen.set(true);
            this.roleplayService.isPostCallVisible.set(true);
            this.roleplayService.footerState.set(RoleplayFooterState.Debrief);
          }
        : undefined,
    );
  }

  handleExitResumed(): void {
    this.showExitModal.set(false);
    this.exitModalIsDebrief.set(false);
    this.exitModalFromHeader.set(false);
    this.callStore?.setDisableEndCallButton(false);
    if (this.controlService) {
      this.controlService.isEndCallTriggered = false;
    }
    this.realtimeService?.cancelEndCallEarly();
  }

  handleOnConfirmActionClick(): void {
    const state = this.roleplayService.footerState();

    if (state === RoleplayFooterState.End) {
      if (this.interactionsMet) {
        this.confirmActionClicked.emit(() => {
          this.isResultScreen.set(true);
          this.roleplayService.isPostCallVisible.set(true);
          this.roleplayService.footerState.set(RoleplayFooterState.Debrief);
        });
      } else {
        this.exitModalFromHeader.set(false);
        this.openEndCallModal();
      }
      return;
    }

    if (state === RoleplayFooterState.Debrief) {
      this.startDebrief((callData) => {
        this.resultsCallData.set(callData);
        this.resultsCallUuid.set(callData.practiceCallUuid);
      });
      return;
    }

    if (state === RoleplayFooterState.MoveToNext) {
      const hasDebrief = !!this.roleplayService.debriefInstanceUuid();
      if (hasDebrief) {
        const cached = this.roleplayService.cachedDebriefResponse();
        if (!cached) {
          this.openDebriefExitModal();
          return;
        }
        this.terminateDebriefConversation();
      }
      this.confirmActionClicked.emit(() => {
        this.roleplayService.prepareForNextStep();
      });
      return;
    }

    if (state === RoleplayFooterState.ExitPathway) {
      const hasDebrief = !!this.roleplayService.debriefInstanceUuid();
      if (hasDebrief) {
        const cached = this.roleplayService.cachedDebriefResponse();
        if (!cached) {
          this.openDebriefExitModal();
          return;
        }
        this.terminateDebriefConversation();
      }
      this.roleplayService.prepareForNextStep();
      this.roleplayService.mainIsLoading.set(true);
      this.leaveStepClicked.emit();
      return;
    }

    if (state === RoleplayFooterState.RetryRoleplay) {
      this.isResultScreen.set(false);
      this.roleplayService.prepareForNextStep();
      const hasActiveDebrief = !!this.roleplayService.debriefInstanceUuid();
      if (hasActiveDebrief) {
        this.terminateDebriefConversation();
      }
      this.retryRoleplayClicked.emit();
      return;
    }
  }

  private resolvePostDebriefState(): RoleplayFooterState {
    const hasFailed = this.roleplayService.hasFailed();
    const isBlocked =
      this.roleplayService.nextStepResponse()?.status ===
      PathwayConstants.API_STATUS_BLOCKED;
    const isStartDebriefFlow =
      this.route.snapshot.queryParams['startDebrief'] === 'true';

    if (!hasFailed && this.roleplayService.nextStepResponse()?.toStepUuid) {
      return RoleplayFooterState.MoveToNext;
    }
    if (hasFailed && !isBlocked && !isStartDebriefFlow) {
      return RoleplayFooterState.RetryRoleplay;
    }
    return RoleplayFooterState.ExitPathway;
  }

  private openEndCallModal(): void {
    const descKey = this.interactionsMet
      ? 'learningPathway.EXIT_MODAL_DESC_SCORED'
      : this.hasLimitedAttempts
        ? 'learningPathway.EXIT_MODAL_DESC_LIMITED_ATTEMPTS'
        : 'learningPathway.EXIT_MODAL_DESC_UNLIMITED_ATTEMPTS';
    this.exitModalTitleKey.set('learningPathway.EXIT_MODAL_TITLE');
    this.exitModalDescriptionKey.set(descKey);
    this.callStore?.setDisableEndCallButton(true);
    if (this.controlService) {
      this.controlService.isEndCallTriggered = true;
    }
    this.realtimeService?.toggleMic(false);
    this.showExitModal.set(true);
  }

  private openDebriefExitModal(): void {
    this.exitModalTitleKey.set('learningPathway.DEBRIEF_EXIT_MODAL_TITLE');
    this.exitModalDescriptionKey.set('learningPathway.DEBRIEF_EXIT_MODAL_DESC');
    this.exitModalIsDebrief.set(true);
    this.realtimeService?.toggleMic(false);
    this.showExitModal.set(true);
  }

  private terminateDebriefConversation(): void {
    this.realtimeService?.terminateConversationAsync().catch(() => {});
  }

  ngOnDestroy(): void {
    this.roleplayService.destroy();
  }
}
